/* global G, PLATFORM */
import { asyncPipeSpread, bulk, getFirstItem, isObj } from 'lib/util'
import find from 'lib/sequence/component/children/find'
import map from 'lib/sequence/model/api/map'
import refresh from 'lib/sequence/model/api/refresh'
import sequenceComponentState from 'lib/sequence/component/state'
import { hide, show } from 'lib/sequence/component/state/hidden'
import { get, set } from 'lib/sequence/component/state/value'
import internalOrganisations from 'model/organisation/collection/internal'
import getUserRole from 'app/_shared/events/user/role'
import session from 'app/_shared/session'
import settings from '@tenant/settings'

const display = x => (get(x) ? show(x) : hide(x))
const bulkDisplay = bulk(display)

const {
  set: decorate,
  unset: undecorate,
} = sequenceComponentState('decorators')

const {
  set: setRef,
} = sequenceComponentState(G.REF)

/**
 * Returns translated text to be displayed in case the value of a field is not set.
 *
 * @param {Gaia.AppModule.Spec} module current module composition
 * @returns {Promise<string>}
 */
const notSpecifiedText = async module => await module[G.ADAPTER][G.INTL]._t(
  'label.notSpecified',
  {
    ns: 'common',
    _key: 'label.notSpecified',
    defaultValue: 'Not specified',
  },
)

/**
 * Attempts to fetch the current user's data and sets it to the current model.
 *
 * @param {Gaia.AppModule.Spec} module current module composition
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const refreshModelCache = module => async (components, ...args) => {
  const model = module[G.MODEL]
  // TODO:  Temporarily, we fetch the latest user all the time and don't rely on a cached one
  //        See: SP-1879
  const { [G.HTTP]: httpAdapter } = model[G.ADAPTER]
  const user = session(module).user.key({ wrap: false })
  const { options: { api = 'user', version = 1 } = {} } = model[G.CONFIGURATION] || {}

  try {
    const result = await httpAdapter.get({ url: `/api/v${version}/${api}/${user}`, params: { verbose: true } })
    result && isObj(result) && await refresh(model, result)
  } catch (e) {
    console.error(e)
  }

  return [components, ...args]
}

/**
 * Sets the avatar image from the user's person.
 *
 * @param {Gaia.AppModule.Spec} module current module composition
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const initAvatar = module => async (components, ...args) => {
  const model = module[G.MODEL]
  const { person } = model[G.CHILDREN]
  const personRef = person[G.STATE][G.REF]

  const { avatar } = components

  if (get(avatar) !== personRef) {
    set(avatar, personRef)
    setRef(avatar, personRef)
  }

  return [components, ...args]
}

/**
 * Maps the current user's role to the view or hides it if they have no role.
 *
 * @param {Gaia.AppModule.Spec} module current module composition
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const displayUserRole = module => async (components, ...args) => {
  const model = module[G.MODEL]
  const userRoles = model[G.CACHE]?.value?.roles || []
  const { component, personHeader, role } = components
  const { businessRole } = find(personHeader)
  const userRole = getUserRole(userRoles, module, component, null)?.value

  userRole && show(role) && set(role, userRole)

  settings.suppressProfileRole
    && hide(businessRole)
    && hide(role)

  return [components, ...args]
}

/**
 * Maps the current user and their person data to the view.
 *
 * @param {Gaia.AppModule.Spec} module current module composition
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const mapModel = module => async (components, ...args) => {
  const model = module[G.MODEL]
  const { person } = model[G.CHILDREN]
  const { organisationItem, teamsButton, title, languageButton, infoList } = components
  const bulkMap = bulk(map(model))
  const bulkMapPerson = bulk(map(person))

  bulkMap(organisationItem, teamsButton)
  bulkMapPerson(title, languageButton, infoList)

  return [components, ...args]
}

/**
 * Hides the user's organisation if it is internal.
 *
 * @param {Gaia.AppModule.Spec} module current module composition
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const displayOrganisation = module => async (components, ...args) => {
  const model = module[G.MODEL]
  const { organisation } = model[G.CHILDREN]
  const { organisationItem } = components

  const isInternal = internalOrganisations.find(x => x.key === organisation[G.STATE][G.REF])
  isInternal && hide(organisationItem)

  return [components, ...args]
}

/**
 * Hides the team section if the current user has no assigned team.
 *
 * @param {Gaia.AppModule.Spec} module current module composition
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const displayTeam = module => async (components, ...args) => {
  const model = module[G.MODEL]
  const { team } = model[G.CHILDREN]
  const { teamsSection } = components

  !team[G.STATE][G.REF] && hide(teamsSection) // && hide(teamsDivider)

  return [components, ...args]
}

/**
 * If the current user has no language selected, sets red text obtained from calling
 * {@link notSpecifiedText} as the language label.
 *
 * @param {Gaia.AppModule.Spec} module current module composition
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const decorateLanguage = module => async (components, ...args) => {
  const model = module[G.MODEL]
  const { person } = model[G.CHILDREN]
  const { langName } = person[G.CHILDREN]
  const { langName: languageLabel } = components

  langName[G.CACHE]
    ? undecorate(languageLabel)
    : decorate(languageLabel, { error: true })
      && set(languageLabel, await notSpecifiedText(module))

  return [components, ...args]
}

/**
 * Hides sections for which the current user has no information.
 *
 * @param {Gaia.AppModule.Spec} module current module composition
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const displayPersonInfo = module => async (components, ...args) => {
  const model = module[G.MODEL]
  const { person } = model[G.CHILDREN]
  const { phone, officePhone, email, fax, website, personButton } = components

  bulkDisplay(phone, officePhone, email, fax, website)

  PLATFORM === 'mobile' && map(person)(personButton)

  return [components, ...args]
}

/**
 * Sets the correct label in the "Authenticated method" section.
 *
 * @param {Gaia.AppModule.Spec} module current module composition
 * @returns {function(*, ...[*]): *[]}
 */
const displayAuthenticationMethod = module => (components, ...args) => {
  const model = module[G.MODEL]
  const { accountTypeButton } = components

  model[G.CACHE]?.value?.passwordGenerated
    && accountTypeButton[G.CONFIGURATION]?.t?.value?.options?._key
    && (accountTypeButton[G.CONFIGURATION].t.value.options._key = 'label.isPasswordGenerated')

  return [components, ...args]
}

/**
 * Displays the formatted address
 *
 * @param {Gaia.AppModule.Spec} module current module composition
 * @returns {function(*, ...[*]): *[]}
 */
const displayAddress = module => (components, ...args) => {
  const { [G.INTL]: intl } = module[G.ADAPTER]
  const organisation = getFirstItem(module[G.MODEL][G.CACHE]?.refs?.organisation || [{}])
  const { value: { address = {} } = {} } = organisation

  const { summary } = components

  summary && set(summary, intl.address(address, { inline: true }))

  return [components, ...args]
}

export default module => component => async (...args) => asyncPipeSpread(
  refreshModelCache(module),
  initAvatar(module),
  displayUserRole(module),
  mapModel(module),
  displayOrganisation(module),
  displayTeam(module),
  decorateLanguage(module),
  displayPersonInfo(module),
  displayAuthenticationMethod(module),
  displayAddress(module),
  (components, ...argv) => argv,
)(find(component), ...args)
